// src/pages/TeamInboxPage.jsx
//
// Team Chat — shared by the Admin, Navigator and Doctor portals (all use the
// same AppShell from components/Layout.js; routed at /team-chat and
// /doctor/team-chat).
//
// The strip lists PEOPLE, not just threads — same shape as
// PatientCareTeamPage.jsx. It is built from two sources, merged:
//
//   - GET /api/conversations/team-directory — everyone the caller can message
//     here, each with the conversationType to use and the existing thread's
//     id (null when there is none yet). A navigator/admin caller gets every
//     doctor with an active login plus every active staff member; a doctor
//     caller gets staff only (there is no doctor-to-doctor conversation type).
//   - GET /api/conversations — the caller's existing threads, for unread
//     counts and last activity, filtered to the three team types
//     (NAVIGATOR_DOCTOR, NAVIGATOR_NAVIGATOR, SUBSCRIBER_NAVIGATOR). A
//     navigator's PATIENT_NAVIGATOR threads live on Patient Messages instead.
//
// A thread whose counterpart is not in the directory still gets its own card:
// subscribers are never in it, and a doctor whose login has been deactivated
// is hidden from it but their conversation history is not.
//
// The directory endpoint is newer than the rest of this page. If it fails for
// any reason (404 on a backend that doesn't have it yet), the strip falls back
// to existing threads only — silently, since the page still works.
//
// Clicking someone with no thread creates it (createOrGet with the
// directory's conversationType) and opens it. Creating a thread never sends a
// message.
//
// Subscribers are not in the directory — there could be hundreds — so Admin
// and Health Navigator callers get "Find a subscriber", the search-based
// SubscriberSearchPicker. GET /api/subscribers/search is ADMIN/HEALTH_NAVIGATOR
// only server-side, so neither a doctor nor SUPPORT_STAFF sees the button.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { chatMessagingAPI } from '../services/chatMessagingAPI';
import { useToast } from '../context/ToastContext';
import { useHumanChat } from '../hooks/useHumanChat';
import HumanChatWindow from '../components/chat/HumanChatWindow';
import SubscriberSearchPicker from '../components/chat/SubscriberSearchPicker';
import avatarTint, { avatarAccentFill } from '../utils/avatarTint';
import EmptyState from '../components/EmptyState';

const TEAM_TYPES = ['NAVIGATOR_DOCTOR', 'SUBSCRIBER_NAVIGATOR', 'NAVIGATOR_NAVIGATOR'];

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// Mirrors PatientCareTeamPage.jsx's displayFirstName — a doctor's `name`
// starts with a "Dr"/"Dr." title, so a bare first-token split only ever
// returns the title and drops the actual name.
function displayFirstName(name, role) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (role === 'DOCTOR' && /^dr\.?$/i.test(parts[0])) {
    return parts.length > 1 ? `Dr. ${parts[1]}` : 'Dr.';
  }
  return parts[0];
}

// staffRole is only known for directory entries; a thread-only card falls
// back to the conversation's counterpartRole.
function roleLabel(role, staffRole) {
  if (role === 'DOCTOR') return 'Doctor';
  if (role === 'SUBSCRIBER') return 'Subscriber';
  if (staffRole === 'ADMIN') return 'Admin';
  if (staffRole === 'SUPPORT_STAFF') return 'Support staff';
  return 'Navigator';
}

// One card per person per conversation type — the same person can't appear
// twice under one type, and the type decides which thread a card opens.
function entryKey(conversationType, counterpartId) {
  return `${conversationType}:${counterpartId}`;
}

function lastActivity(e) {
  return new Date(e.lastMessageAt || e.createdAt || 0).getTime();
}

// People with recent activity first, then everyone else by name.
function sortEntries(list) {
  return [...list].sort((a, b) => (
    lastActivity(b) - lastActivity(a) || (a.name || '').localeCompare(b.name || '')
  ));
}

export default function TeamInboxPage() {
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';
  const canFindSubscribers = user?.role === 'ADMIN' || user?.role === 'HEALTH_NAVIGATOR';
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  // false when the directory couldn't be loaded and the strip is showing
  // existing threads only — changes what the empty state can promise.
  const [directoryLoaded, setDirectoryLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [subscriberPickerOpen, setSubscriberPickerOpen] = useState(false);
  // 'ALL' | 'DOCTOR' | 'NAVIGATOR' | 'SUBSCRIBER' — the strip's own filter,
  // same pattern as PatientCareTeamPage.jsx's.
  const [filter, setFilter] = useState('ALL');
  // Key of the card whose thread is being created. A ref guards against a
  // second click landing before the state update re-renders the button as
  // disabled — one click must send exactly one createOrGet.
  const [startingKey, setStartingKey] = useState(null);
  const startingRef = useRef(null);
  const [searchParams] = useSearchParams();

  const loadEntries = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      chatMessagingAPI.teamDirectory(),
      chatMessagingAPI.list(),
    ])
      .then(([dirRes, convRes]) => {
        const directoryOk = dirRes.status === 'fulfilled' && Array.isArray(dirRes.value);
        const listOk = convRes.status === 'fulfilled' && Array.isArray(convRes.value);
        if (!directoryOk && !listOk) {
          setError(convRes.reason?.message || 'Could not load your team messages.');
          return;
        }

        const threads = listOk
          ? convRes.value.filter(c => TEAM_TYPES.includes(c.conversationType))
          : [];
        const threadById = new Map(threads.map(t => [t.id, t]));
        const threadByKey = new Map(threads.map(t => [entryKey(t.conversationType, t.counterpartId), t]));

        // The backend already leaves doctors out of a doctor caller's
        // directory; dropping them here too keeps a card from offering a
        // doctor-to-doctor thread that cannot be created.
        const directory = directoryOk
          ? dirRes.value.filter(p => !(isDoctor && p.role === 'DOCTOR'))
          : [];

        const people = directory.map(p => {
          const key = entryKey(p.conversationType, p.id);
          const thread = (p.conversationId && threadById.get(p.conversationId)) || threadByKey.get(key);
          return {
            key,
            id: p.id,
            name: p.name,
            role: p.role,
            staffRole: p.staffRole,
            conversationType: p.conversationType,
            // conversationId may be null — that is "no thread yet", not an error.
            conversationId: p.conversationId || thread?.id || null,
            unreadCount: thread?.unreadCount || 0,
            lastMessageAt: thread?.lastMessageAt || null,
            createdAt: thread?.createdAt || null,
          };
        });

        const coveredIds = new Set(people.map(e => e.conversationId).filter(Boolean));
        const coveredKeys = new Set(people.map(e => e.key));
        const threadOnly = threads
          .filter(t => !coveredIds.has(t.id) && !coveredKeys.has(entryKey(t.conversationType, t.counterpartId)))
          .map(t => ({
            key: entryKey(t.conversationType, t.counterpartId),
            id: t.counterpartId,
            name: t.counterpartName,
            role: t.counterpartRole,
            staffRole: null,
            conversationType: t.conversationType,
            conversationId: t.id,
            unreadCount: t.unreadCount || 0,
            lastMessageAt: t.lastMessageAt || null,
            createdAt: t.createdAt || null,
          }));

        const fresh = [...people, ...threadOnly];
        setEntries(prev => {
          // A thread created here has no messages yet, and the conversation
          // list leaves zero-message threads out (DEF-033) — so a refetch
          // right after createOrGet doesn't know about it. Keep the locally
          // attached id rather than letting the refetch snap the open pane
          // back to "No conversation open"; same reasoning as
          // PatientCareTeamPage.jsx's stillPending.
          const prevByKey = new Map(prev.map(e => [e.key, e]));
          const merged = fresh.map(e => (
            e.conversationId ? e : { ...e, conversationId: prevByKey.get(e.key)?.conversationId || null }
          ));
          const freshKeys = new Set(merged.map(e => e.key));
          const stillPending = prev.filter(e => e.conversationId && !freshKeys.has(e.key));
          return sortEntries([...merged, ...stillPending]);
        });
        setDirectoryLoaded(directoryOk);
        setError(null);
      })
      .finally(() => setLoading(false));
  }, [isDoctor]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // autoSelectedRef also guards the deep-link effect right below — see
  // PatientCareTeamPage.jsx, which has the identical pair.
  const autoSelectedRef = useRef(false);

  // Deep-link from the notification bell (?conversationId=...) — see
  // useNotifications.js. Without this, a team-chat notification's "to" link
  // could only ever point at this page in general, so clicking it just
  // opened whichever conversation the auto-select effect below had already
  // picked — not the one the notification named.
  useEffect(() => {
    const cid = searchParams.get('conversationId');
    if (!cid) return;
    setActiveId(cid);
    autoSelectedRef.current = true;
  }, [searchParams]);

  // Opens the most recent thread once, on initial load. Only people who
  // already have a thread qualify — opening anyone else would create a
  // conversation nobody asked for.
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (activeId) return;
    const withThreads = entries.filter(e => e.conversationId);
    if (withThreads.length === 0) return;
    const mostRecent = withThreads.reduce((latest, e) => (
      lastActivity(e) > lastActivity(latest) ? e : latest
    ));
    setActiveId(mostRecent.conversationId);
    autoSelectedRef.current = true;
  }, [entries, activeId]);

  // `activeId ?` matters: a card with no thread has conversationId null, and
  // a bare find() would match null === null — see PatientCareTeamPage.jsx.
  const active = activeId ? (entries.find(e => e.conversationId === activeId) || null) : null;
  const chat = useHumanChat(activeId);

  // Records a freshly created thread against its card, adding the card when
  // the person isn't in the strip yet (a subscriber picked from search).
  function attachConversation(key, conversationId, person) {
    setEntries(prev => {
      if (prev.some(e => e.key === key)) {
        return prev.map(e => (e.key === key ? { ...e, conversationId } : e));
      }
      return sortEntries([...prev, {
        key,
        ...person,
        conversationId,
        unreadCount: 0,
        lastMessageAt: null,
        createdAt: null,
      }]);
    });
  }

  // Opens a card's thread, creating it on first use.
  async function openEntry(entry) {
    if (entry.conversationId) {
      setActiveId(entry.conversationId);
      return;
    }
    if (startingRef.current) return;
    startingRef.current = entry.key;
    setStartingKey(entry.key);
    try {
      const conversation = await chatMessagingAPI.createOrGet(entry.conversationType, entry.id);
      attachConversation(entry.key, conversation.id);
      setActiveId(conversation.id);
      loadEntries();
    } catch (e) {
      toast.error(e.message || 'Could not start that conversation.');
    } finally {
      startingRef.current = null;
      setStartingKey(null);
    }
  }

  // The picker only lets an ACTIVE-plan subscriber through (requireActivePlan),
  // so an error here is a real failure, not the expected eligibility rule.
  async function handlePickSubscriber(subscriber) {
    try {
      const conversation = await chatMessagingAPI.createOrGet('SUBSCRIBER_NAVIGATOR', subscriber.id);
      setSubscriberPickerOpen(false);
      attachConversation(entryKey('SUBSCRIBER_NAVIGATOR', subscriber.id), conversation.id, {
        id: subscriber.id,
        name: subscriber.name,
        role: 'SUBSCRIBER',
        staffRole: null,
        conversationType: 'SUBSCRIBER_NAVIGATOR',
      });
      if (filter !== 'ALL' && filter !== 'SUBSCRIBER') setFilter('SUBSCRIBER');
      setActiveId(conversation.id);
      loadEntries();
    } catch (e) {
      toast.error(e.message || 'Could not start that conversation.');
    }
  }

  // Same All / Doctors / Navigators row as PatientCareTeamPage.jsx, plus
  // Subscribers for non-doctors: a doctor never has a SUBSCRIBER_NAVIGATOR
  // thread.
  const filterOptions = [
    ['ALL', 'All'], ['DOCTOR', 'Doctors'], ['NAVIGATOR', 'Navigators'],
    ...(isDoctor ? [] : [['SUBSCRIBER', 'Subscribers']]),
  ];
  const matchesFilter = (e, value) => value === 'ALL' || e.role === value;
  const filteredEntries = entries.filter(e => matchesFilter(e, filter));

  // Switching tabs keeps the open thread when it's in the new list, and
  // otherwise opens the new list's first EXISTING thread. It never opens a
  // card without one — that would create a conversation just by browsing tabs.
  function selectFilter(value) {
    setFilter(value);
    const list = entries.filter(e => matchesFilter(e, value));
    if (active && matchesFilter(active, value)) return;
    const firstThread = list.find(e => e.conversationId);
    if (firstThread) setActiveId(firstThread.conversationId);
  }

  function emptyFilterText() {
    if (filter === 'SUBSCRIBER') {
      return canFindSubscribers
        ? 'No subscriber conversations yet. Use “Find a subscriber” to start one.'
        : 'No subscriber conversations yet.';
    }
    if (filter === 'DOCTOR' && isDoctor) return 'Team Chat doesn’t include doctor-to-doctor messages.';
    return filter === 'DOCTOR' ? 'No doctors to show.' : 'No navigators to show.';
  }

  function emptyStripMessage() {
    if (directoryLoaded) {
      return isDoctor
        ? 'Staff appear here once they have an active login.'
        : 'Your teammates, and doctors with an active login, appear here.';
    }
    return canFindSubscribers
      ? 'Your conversations appear here. Use “Find a subscriber” to message a subscriber.'
      : 'Your conversations appear here once one is started.';
  }

  return (
    <AppShell breadcrumbs={[{ label: 'Team Chat' }]}>
      {/* Same horizontal filterable avatar strip as PatientCareTeamPage.jsx —
          compact at any width, so no two-pane collapse breakpoint or
          page-local mobile CSS is needed here either. */}
      <div className="chat-pane" style={{ flexDirection: 'column', gap: 14 }}>
        {/* People strip */}
        <div className="panel" style={{ flexShrink: 0, padding: '14px 16px' }}>
          <div className="panel-head" style={{ marginBottom: canFindSubscribers ? 10 : 16, flexWrap: 'nowrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '.08em', flexShrink: 0,
            }}>
              Team Chat
            </span>

            <div className="tabs-bar" style={{ margin: 0, flexShrink: 1, minWidth: 0 }}>
              {filterOptions.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`tab-btn${filter === value ? ' active' : ''}`}
                  aria-pressed={filter === value}
                  onClick={() => selectFilter(value)}
                  style={{
                    padding: '6px 12px', fontSize: 12.5,
                    ...(filter === value ? { background: 'var(--teal)', color: 'var(--brand-dark)' } : null),
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Its own row rather than in the head: the head is already title +
              four tabs, which only just fits a phone. */}
          {canFindSubscribers && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setSubscriberPickerOpen(true)}
              >
                Find a subscriber
              </button>
            </div>
          )}

          {loading && entries.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
          )}
          {error && (
            <div style={{ fontSize: 13, color: 'var(--red)' }}>{error}</div>
          )}
          {!loading && !error && entries.length === 0 && (
            <EmptyState
              compact
              icon="chat"
              title={directoryLoaded ? 'No one to message yet' : 'No conversations yet'}
              message={emptyStripMessage()}
            />
          )}
          {!loading && !error && entries.length > 0 && filteredEntries.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {emptyFilterText()}
            </div>
          )}

          <div style={{
            display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 2,
            scrollSnapType: 'x mandatory',
          }}>
            {filteredEntries.map((e) => {
              const isActive = !!e.conversationId && e.conversationId === activeId;
              const isStarting = startingKey === e.key;
              const accent = avatarAccentFill(e.name);
              const label = roleLabel(e.role, e.staffRole);
              return (
                <button
                  key={e.key}
                  type="button"
                  onClick={() => openEntry(e)}
                  disabled={isStarting}
                  aria-label={`${e.name}, ${label}${e.unreadCount > 0 ? `, ${e.unreadCount} unread` : ''}`}
                  title={`${e.name} — ${label}`}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '10px 8px 8px', cursor: isStarting ? 'progress' : 'pointer',
                    flex: '0 0 calc((100% - 48px) / 5)', minWidth: 96, scrollSnapAlign: 'start',
                    background: 'var(--bg-subtle)',
                    border: isActive ? `2px solid ${accent}` : '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <div
                      className={`avatar ${avatarTint(e.name)}`}
                      aria-hidden="true"
                      style={{
                        width: 40, height: 40, fontSize: 14,
                        boxShadow: isActive ? `0 0 0 2px var(--bg-subtle), 0 0 0 4px ${accent}` : 'none',
                      }}
                    >
                      {initials(e.name)}
                    </div>
                    {e.unreadCount > 0 && (
                      <span aria-hidden="true" style={{
                        position: 'absolute', top: -2, right: -2, minWidth: 17, height: 17,
                        borderRadius: 999, background: 'var(--red)', color: 'var(--text-on-brand-dark)',
                        fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', padding: '0 4px', border: '2px solid var(--bg-subtle)',
                      }}>
                        {e.unreadCount}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: 'var(--text-primary)',
                    textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', width: '100%',
                  }}>
                    {displayFirstName(e.name, e.role)}
                  </div>
                  <div style={{
                    fontSize: 11, fontWeight: 600,
                    color: isActive ? accent : 'var(--text-muted)',
                  }}>
                    {isStarting ? 'Opening…' : label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active thread */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {active ? (
            <HumanChatWindow
              counterpartName={active.name}
              counterpartRole={active.role}
              counterpartId={active.id}
              messages={chat.messages}
              loading={chat.loading}
              error={chat.error}
              sending={chat.sending}
              onSend={chat.sendMessage}
            />
          ) : (
            <div className="panel" style={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EmptyState
                icon="chat"
                title="No conversation open"
                message="Pick someone from the strip above to open your conversation with them."
              />
            </div>
          )}
        </div>
      </div>

      {subscriberPickerOpen && (
        <SubscriberSearchPicker
          requireActivePlan
          onSelect={handlePickSubscriber}
          onClose={() => setSubscriberPickerOpen(false)}
        />
      )}
    </AppShell>
  );
}
